import createRequest from 'airship/modules/request2/ua-request'
import getUrl from '../modules/get-url'
import reportError from 'airship/modules/report-error'

const debug = require('airship/modules/debug')

const BROWSER_HAS_PERFORMANCE = Boolean(
  performance && performance.getEntriesByType
)
const DEBUG = debug()

// Disabling for now to see if it helps our GKE prometheus instance stay
// healthy.
const ENABLED = false

interface PerformanceMetrics {
  domComplete: number
  domInteractive: number
}

// https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming
interface ExtendedPerformanceEntry
  extends PerformanceEntry,
    PerformanceMetrics {}

let reported = false

async function report(): Promise<void> {
  if (!ENABLED) {
    return
  }
  // We don't need to fire this request on every page view.
  if (Math.random() > 0.3 && !DEBUG) {
    return
  }
  try {
    await sendReport()
  } catch (err) {
    reportError(err)
  }
}

async function sendReport(): Promise<void> {
  if (!BROWSER_HAS_PERFORMANCE) {
    return
  }

  if (reported) {
    return
  }
  reported = true

  const metrics = await getPerformanceMetrics()
  const path = getUrl('metrics:javascript_metrics')
  const body = {
    path: window.location.pathname,
    metrics,
  }
  const req = createRequest({path})
  const res = await req.post({body})
  req.end()

  if (res.statusCode < 200 || res.statusCode > 299) {
    throw new Error(`JS Metrics API returned ${res.statusCode}`)
  }
}

/**
 * Returns a subset of page-load performance numbers.
 */
export function getPerformanceMetrics(
  perfs?: PerformanceEntry[]
): Promise<PerformanceMetrics> {
  return new Promise((resolve) => {
    const interval = setInterval(compute, 200)

    function compute(): void {
      if (!perfs) {
        perfs = performance.getEntriesByType('navigation')
        if (!perfs || !perfs.length) {
          return
        }
      }

      // I'm not sure what the numbers look like when there are multiple
      // entries. For now, just process the first. If that's insufficient,
      // figure out how to handle multiples.
      const perf = perfs[0] as ExtendedPerformanceEntry
      if (!perf || !perf.domComplete) {
        return
      }

      clearInterval(interval)

      // Divide timings into seconds so that they fit into our existing
      // prometheus buckets.
      resolve({
        domComplete: perf.domComplete / 1000,
        domInteractive: perf.domInteractive / 1000,
      })
    }
  })
}

window.addEventListener('load', report)
